'use client'

import clsx from 'clsx'
import Icon from 'lib/components/Icon/Icon'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  ChangeEventHandler,
  HTMLProps,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import InputError from '../InputError/InputError'
import InputLabel from '../InputLabel/InputLabel'

interface NumberInputStandaloneProps
  extends Omit<HTMLProps<HTMLInputElement>, 'label' | 'onChange'>,
    Omit<FormFieldCommonProps, 'wrapperClassname' | 'onChange'> {
  /**
   * Optional maximum number limit, which integrates built-in form validation.
   * If parent form has `noValidate` attribute, custom error message will be used.
   */
  max?: number
  /**
   * Whether to use simple UI (no +/- buttons, similar to TextInput)
   */
  simple?: boolean
  /**
   * Any additional add-on element to appear beside the label
   */
  addOn?: ReactNode
  /**
   * Optional default value of the input
   */
  defaultValue?: number | string
  /**
   * Optional callback that runs when the input value changes
   */
  onChange?: (data?: string | number, fieldName?: string) => void
  /**
   * Optional prefix text (e.g., currency) for simple UI variant
   */
  prefix?: string
}

export interface NumberInputProps
  extends Omit<ControlledFormField, 'defaultValue' | 'onChange'>,
    NumberInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const NumberInputStandalone = forwardRef<
  HTMLInputElement,
  NumberInputStandaloneProps
>(function NumberInputStandaloneWithRef(
  {
    label,
    name,
    max,
    defaultValue = 0,
    id = name,
    className,
    onChange,
    error,
    placeholder,
    simple,
    step,
    addOn,
    prefix,
    ...props
  }: NumberInputStandaloneProps,
  ref,
) {
  const customRef = useRef<HTMLInputElement | undefined>(undefined)

  const [defaultValueCached, setDefaultValueCached] = useState(defaultValue)
  const [currentValue, setCurrentValue] = useState<number | string>(
    defaultValue,
  )

  const onNumberChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setCurrentValue(target.value ? Number(target.value) : '')
    onChange?.(Number(target.value))
  }

  const onNumberBlur: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setCurrentValue(target.value ? Number(target.value) : 0)

    if (simple) {
      onChange?.(Number(target.value))
    }
  }

  const setValue = useCallback(
    (value: number) => {
      setCurrentValue(value)
      onChange?.(value, name)
    },
    [onChange, name],
  )

  const add = () => setValue(currentValue !== '' ? Number(currentValue) + 1 : 1)
  const subtract = () =>
    setValue(currentValue !== '' ? Number(currentValue) - 1 : 0)

  // Handles defaultValue change from the outside
  useEffect(() => {
    if (defaultValue !== defaultValueCached) {
      if (customRef.current) {
        customRef.current.value = defaultValue as string

        if (typeof ref === 'function') {
          ref(customRef.current)
        }
      }

      setDefaultValueCached(defaultValue)
      setCurrentValue(defaultValue)
    }
  }, [defaultValue, defaultValueCached, ref, customRef, onChange, name])

  return simple ? (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        {addOn}
      </div>
      <div
        className={clsx(
          'text-input-content flex items-stretch w-full rounded-8 my-2 transition-shadow bg-white px-4 type-body-8 text-off-shore-navy',
          'has-[:disabled]:bg-neutral-200 has-[:disabled]:shadow-input-disabled has-[:disabled]:cursor-not-allowed has-[:disabled]:text-neutral-600',
          'hover:shadow-input-active',
          'has-[:focus]:shadow-input-focus',
          error ? '!shadow-input-error' : 'shadow-input',
        )}
      >
        {prefix ? <div className="flex items-center">{prefix}</div> : ''}
        <input
          type="number"
          className={clsx(
            'flex-1 h-12 flex items-center lg:h-14 bg-transparent w-full appearance-none',
            'focus:outline-none',
          )}
          placeholder={placeholder}
          name={name}
          id={id}
          defaultValue={defaultValueCached}
          ref={ref}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          step={step}
        />
      </div>
      <InputError>{error}</InputError>
    </div>
  ) : (
    <div className={clsx('w-full', className)}>
      <div
        className={clsx(
          'group inline-flex items-center type-body-7 border border-solid transition-colors rounded-8 w-full h-[55px] px-2 lg:px-2 text-neutral-700 gap-x-[10px] overflow-hidden data-[hover]:border-theme-secondary border-neutral-500',
          { 'lg:hover:border-theme-secondary': !error },
          currentValue
            ? `[&]:border-theme-secondary [&]:bg-theme-accent-1`
            : error
              ? '[&]:border-error [&]:focus-within:border-error border-2 bg-white'
              : '[&]:focus-within:border-theme-secondary [&]:border-neutral-500 bg-white',
        )}
      >
        <label
          className="whitespace-nowrap overflow-hidden text-ellipsis"
          htmlFor={id}
        >
          {label}
        </label>
        <div className="inline-flex items-center ml-auto mr-0">
          <button
            type="button"
            className="w-4 h-4 inline-flex items-center justify-center disabled:text-neutral-500"
            disabled={
              currentValue !== '' ? Number(currentValue) <= 0 : undefined
            }
            onClick={subtract}
            aria-label={`Decrease ${label} count`}
          >
            <Icon icon="remove-thick" className="w-3 h-3" />
          </button>
          <input
            type="number"
            name={name}
            id={id}
            min={0}
            max={max}
            value={currentValue}
            onChange={onNumberChange}
            onBlur={onNumberBlur}
            style={{
              width: `${(currentValue ? Number(currentValue).toFixed(0).length : 1) * 10}px`,
            }}
            ref={ref}
            className="inline-flex bg-transparent mx-2 appearance-none text-center text-sm md:text-base"
            {...props}
          />
          <button
            type="button"
            className="w-4 h-4 inline-flex items-center justify-center disabled:text-neutral-500"
            disabled={
              currentValue !== '' && max
                ? Number(currentValue) >= max
                : undefined
            }
            onClick={add}
            aria-label={`Increase ${label} count`}
          >
            <Icon icon="add-thick" className="w-3 h-3" />
          </button>
        </div>
      </div>
      {error && (
        <p className="type-body-10 text-input-error text-error mt-2">{error}</p>
      )}
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function NumberInput({
  name,
  control,
  label = '',
  defaultValue = 0,
  rules,
  max,
  onChange,
  ...props
}: NumberInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={{
        max: max && { value: max, message: 'Maximum amount reached' },
        ...rules,
      }}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }
        return (
          <NumberInputStandalone
            {...{ label, max, defaultValue }}
            {...field}
            onChange={onInputChange}
            error={fieldState.error?.message}
            {...props}
          />
        )
      }}
    />
  )
}
